import type { CreatureCarryingCapacityInput, CreatureCarryingCapacityResult, CreatureCapacityValue } from '../types';
import { readCreatureStatuses } from './creature-status-reader';
import { readItemField } from './item-field-reader';

const finite = (value: number): number => Number.isFinite(value) ? value : 0;
const integer = (value: number): number => {
    const result = Math.trunc(finite(value));
    return Object.is(result, -0) ? 0 : result;
};
/** Positive finite capacity factors apply; absent, invalid and nonpositive values have no effect. */
export function carryingCapacityFactor(value: unknown): number {
    const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 1;
}

/** Keep the native integer only when rounding would make division lose it; existing lossless stamps retain their shape. */
function scaledCapacityPart(unscaled: number, factor: number, breakdown: Record<string, number>): CreatureCapacityValue {
    const total = integer(unscaled * factor);
    if (factor !== 1) {
        breakdown.carrying_capacity_multiplier = factor;
        if (Math.trunc(total / factor) !== unscaled) breakdown.unscaled = unscaled;
    }
    return { total, breakdown };
}

/** Compile the five body-and-size carrying-capacity records after layer/size resolution. */
export function compileCreatureCarryingCapacity(input: CreatureCarryingCapacityInput): CreatureCarryingCapacityResult {
    const body = integer(input.body);
    const weightMultiplier = finite(input.weightMultiplier);
    const weightHeld = integer(input.weightHeld);
    const factor = carryingCapacityFactor(input.carryingCapacityMultiplier);
    const capacity = (base: number): CreatureCapacityValue => scaledCapacityPart(integer(weightMultiplier * (base + body)), factor,
        { base, body, weight_multiplier: integer(weightMultiplier) });
    const encumbered = capacity(5);
    const heavilyEncumbered = capacity(10);
    const max = capacity(15);
    const pushDragLift = capacity(30);
    return {
        encumbered, heavily_encumbered: heavilyEncumbered, max, push_drag_lift: pushDragLift,
        remaining: { total: max.total - weightHeld, breakdown: { max: max.total, weight_held: weightHeld } },
    };
}

type Row = Record<string, unknown>;
const isObj = (v: unknown): v is Row => v !== null && typeof v === 'object' && !Array.isArray(v);
/** The four body-and-size capacity parts the multiplier scales (remaining follows max). */
export const CAPACITY_SCALED_PARTS = ['encumbered', 'heavily_encumbered', 'max', 'push_drag_lift'] as const;

/** The factor a compiled capacity cluster already carries (its parts' breakdown stamp), 1 when unscaled. */
export function compiledCapacityFactor(capacity: unknown): number {
    if (!isObj(capacity)) return 1;
    for (const part of CAPACITY_SCALED_PARTS) {
        const entry = capacity[part];
        const bd = isObj(entry) && isObj(entry.breakdown) ? entry.breakdown : null;
        if (bd && 'carrying_capacity_multiplier' in bd) return carryingCapacityFactor(bd.carrying_capacity_multiplier);
    }
    return 1;
}

/**
 * Rescale a COMPILED capacity cluster (computed.creature.capacity) from its current factor to `factor` — the C-PI fold's apply (from 1)
 * and strip (to 1). Recover the native integer from its rounding stamp, or divide a lossless / legacy total; `remaining` follows max.
 * Returns null when the shape is not the compiled one
 * (the caller leaves the cluster untouched).
 */
export function rescaleCompiledCapacity(capacity: unknown, factor: number): Row | null {
    if (!isObj(capacity)) return null;
    const from = compiledCapacityFactor(capacity), to = carryingCapacityFactor(factor);
    const out: Row = JSON.parse(JSON.stringify(capacity)) as Row;
    for (const part of CAPACITY_SCALED_PARTS) {
        const entry = out[part];
        if (!isObj(entry) || typeof entry.total !== 'number' || !Number.isFinite(entry.total)) return null;
        const bd = isObj(entry.breakdown) ? { ...entry.breakdown } as Record<string, number> : {};
        const unscaled = from !== 1 && Number.isInteger(bd.unscaled) ? bd.unscaled! : integer(entry.total / from);
        delete bd.carrying_capacity_multiplier;
        delete bd.unscaled;
        out[part] = { ...entry, ...scaledCapacityPart(unscaled, to, bd) };
    }
    const remaining = out.remaining;
    if (isObj(remaining) && isObj(remaining.breakdown) && typeof remaining.breakdown.weight_held === 'number') {
        const max = (out.max as Row).total as number, held = remaining.breakdown.weight_held as number;
        out.remaining = { ...remaining, total: max - held, breakdown: { ...remaining.breakdown, max } };
    }
    return out;
}

// ---- R-209: the WEIGHT-TIER encumbrance decision (Wizard v9 WP2, 2026-10-09) ------------------------------------------------------------
// apply_encumbrance_status_effects_to_item_arr()'s three weight tiers, decided by the browser from the row's COMPILED (browser-scaled)
// capacity: Encumbered at weight ≥ encumbered, Heavily Encumbered at ≥ heavily_encumbered, Immobile at ≥ max. Exempt (all off): a dead
// creature, a merchant (the `merchant` layer or a persisted merchant_favor), a campaign running "Inventory: No Encumbrance"; an immune
// status is never on. The page persists the decision through the status commit (websocket/encumbrance-heal.ts); PHP's finalize steps
// aside for exactly the rows that author the layer (finalize_creature_arr.php ft_encumbrance_weight_tiers_browser_owned — twin of the C-PI collection's
// capacity.present, which the healer hands the page as `capacityOwned`).
export const WEIGHT_ENCUMBRANCE_STATUS_IDS = {
    encumbered: '306CD53D-AD03-DD13-FDDF-34D9120613CD',
    heavily_encumbered: '49F87D1B-3878-789E-D52E-65BA7F2F4218',
    immobile: 'F40E19A3-FC86-187F-050E-8C8B69761353',
} as const;
export type WeightEncumbranceTier = keyof typeof WEIGHT_ENCUMBRANCE_STATUS_IDS;
export type WeightEncumbranceState = Record<WeightEncumbranceTier, boolean>;
/** CampaignHouseRuleType no_encumbrance ("Inventory: No Encumbrance"). */
export const NO_ENCUMBRANCE_HOUSE_RULE_ID = 'F1F5285D-E498-4B20-956C-C4CC51015DFC';
const CAPACITY_PART: Record<WeightEncumbranceTier, string> = { encumbered: 'encumbered', heavily_encumbered: 'heavily_encumbered', immobile: 'max' };
const up = (v: unknown): string => typeof v === 'string' ? v.trim().toUpperCase() : '';
const num = (v: unknown): number | null => { const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN; return Number.isFinite(n) ? n : null; };
const scalarOf = (v: unknown): unknown => { let x = v; while (Array.isArray(x)) x = x[0]; return isObj(x) && '0' in x ? x['0'] : x; };

export interface WeightEncumbranceInput {
    row: Row;
    layerValue: (row: Row, name: string) => unknown;
    layerEntries: (row: Row, name: string) => unknown[];
    /** the creature's campaign row; null = it has none (no house rules); undefined = unknown (the decision is withheld) */
    campaign: Row | null | undefined;
}
/** The desired weight tiers, or null when the page cannot decide (no encounter row, no compiled capacity, an unknown campaign). */
export function decideWeightEncumbrance(input: WeightEncumbranceInput): WeightEncumbranceState | null {
    const { row } = input;
    const ec = isObj(row.encounter_creatures) ? row.encounter_creatures as Row : null;
    if (!ec || up(ec.encounter_creature_id) === '') return null;
    const none: WeightEncumbranceState = { encumbered: false, heavily_encumbered: false, immobile: false };
    if (num(scalarOf(ec.encounter_creature_dead)) ?? 0) return none;
    if (input.campaign === undefined) return null;
    const rules = isObj(input.campaign) ? input.campaign.campaign_house_rules : undefined;
    if (Array.isArray(rules) && rules.some(r => up(isObj(r) ? r.campaign_house_rule_id : r) === NO_ENCUMBRANCE_HOUSE_RULE_ID)) return none;
    const merchant = scalarOf(input.layerValue(row, 'merchant'));
    if ((merchant !== null && merchant !== undefined && merchant !== '' && merchant !== 0 && merchant !== '0') || (ec.merchant_favor !== undefined && ec.merchant_favor !== null)) return none;
    const creature = isObj(row.computed) && isObj((row.computed as Row).creature) ? (row.computed as Row).creature as Row : null;
    const capacity = creature && isObj(creature.capacity) ? creature.capacity as Row : null;
    const weight = num(scalarOf(isObj(creature?.weight) ? (creature!.weight as Row).total_weight_held
        : input.layerValue(row, 'total_weight_held'))) ?? 0;
    if (!capacity) return null;
    const immune = new Set(input.layerEntries(row, 'immune_status_effect').map(e => up(isObj(e) ? (e.item_layer_effect ?? (isObj(e.items) ? (e.items as Row).item_id : e.item_id)) : typeof e === 'string' ? e.split('|', 1)[0] : '')));
    const out = { ...none };
    for (const tier of Object.keys(WEIGHT_ENCUMBRANCE_STATUS_IDS) as WeightEncumbranceTier[]) {
        const part = capacity[CAPACITY_PART[tier]];
        const limit = isObj(part) ? num(part.total) : null;
        if (limit === null) return null;
        out[tier] = weight >= limit && !immune.has(WEIGHT_ENCUMBRANCE_STATUS_IDS[tier]);
    }
    return out;
}
/** The pass owns its item-id stamp and pre-stamp blank origins. */
export function isOwnEncumbranceStatus(status: Row): boolean {
    const origin = up(readItemField(status, 'status_origin_id'));
    return origin === '' || origin === up(readItemField(status, 'item_id'));
}
export function ownWeightEncumbranceTier(status: Row): WeightEncumbranceTier | null {
    if ((num(readItemField(status, 'status_deleted')) ?? 0) !== 0) return null;
    const item = up(readItemField(status, 'item_id'));
    if (!isOwnEncumbranceStatus(status)) return null;
    return (Object.keys(WEIGHT_ENCUMBRANCE_STATUS_IDS) as WeightEncumbranceTier[])
        .find(tier => item === WEIGHT_ENCUMBRANCE_STATUS_IDS[tier]) ?? null;
}
/** This pass's weight-tier rows, including pre-stamp rows: tier → status ids. */
export function heldWeightEncumbrance(row: Row): Record<WeightEncumbranceTier, string[]> {
    const out: Record<WeightEncumbranceTier, string[]> = { encumbered: [], heavily_encumbered: [], immobile: [] };
    for (const s of Object.values(readCreatureStatuses(row, 'target'))) {
        if (!isObj(s)) continue;
        const tier = ownWeightEncumbranceTier(s), id = up(readItemField(s, 'status_id'));
        if (tier && id) out[tier].push(id);
    }
    return out;
}
